import { Fragment, memo, useCallback, useMemo, useState } from 'react';

import { AnimatePresence, motion } from 'motion/react';
import PropTypes from 'prop-types';

import { Label } from '@/shadcn/label';
import { cn } from '@/shadcn/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/popover';
import { Skeleton } from '@/shadcn/skeleton';
import { Switch } from '@/shadcn/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import ContextMenu from '@/components/ui/utils/contextMenu';
import { env } from '@/constant/env';
import { formatNumber } from '@/utils/numberFormat';
import { getAggregationConfig } from '../aggregationRow';
import { DisplayMenu } from '../displayMenu/displayMenu';
import { ExportTableMenu } from '../exportTableMenu/exportTableMenu';
import SavedViewsMenu from '../savedViewsMenu';
import {
  HEADER_CHECKBOX,
  HEADER_CHECKBOX_INDETERMINATE,
  TableHeaderElement
} from '../tableHeaderElement';
import { ArchiveDisplayMenu } from './archiveDisplayMenu';

const columnsHaveAggregationSetup = columns =>
  !!columns && columns.some(column => !!getAggregationConfig(column));

const getArchiveLabel = archiveClicked =>
  archiveClicked ? 'Unarchive' : 'Archive';

// table headers
const TableHeaders = memo(
  ({
    toggleFilters,
    isEditing,
    toggleEdition,
    tableConfig,
    displayConfig,
    toggleColumnSelection,
    setGroupBy,
    nbRows,
    update,
    displayDiffs,
    toggleDisplayDiffs,
    onExport,
    onDeleteSelectedRows,
    showAggregation = false,
    toggleShowAggregation,
    nbRowsSelected,
    toggleSelectAll,
    resetDisplayConfig = null,
    shrinkRightIcons = false,
    saveConfig = null,
    updateUserDisplayConfig = null,
    changeTypeFile = null,
    fileType = 'csv',
    headerConfig = {},
    onClickCompareSelectedRows
  }) => {
    const [showSavedViewsMenu, setShowSavedViewsMenu] = useState(false);

    const {
      onClickConfig,
      configClicked,
      onClickNew,
      onClickArchive,
      archiveClicked,
      newClicked,
      onClickUpdate,
      updateClicked,
      updateRowsArchiveStatus,
      edit: { allowDisplayDiffs, onClickBatchEdit, batchEditClicked } = {},
      pagination,
      group,
      matchingCount = 0,
      updateDisabledReason = null
    } = update;

    // "Update" button label: selection wins when any row is checked (explicit
    // batch is what the user picked). Otherwise fall back to the filter-scan
    // matching count if a filter is active. Never show both — the label reads
    // as one clear intent at a glance.
    //   0 selected, 0 matching   -> "Update"
    //   3 selected, ANY matching -> "Update 3 selected"
    //   0 selected, 28 matching  -> "Update 28 matching"
    const updateButtonLabel = useMemo(() => {
      if (nbRowsSelected > 0) {
        return `Update ${formatNumber(nbRowsSelected)} selected`;
      }
      if (matchingCount > 0) {
        return `Update ${formatNumber(matchingCount)} matching`;
      }
      return 'Update';
    }, [nbRowsSelected, matchingCount]);

    const { allowColumnsSelection = true } = tableConfig;
    const {
      applyFilters: showFilters = false,
      fixedColumns,
      columns
    } = displayConfig;

    const toggleShowSavedViewsMenu = useCallback(() => {
      setShowSavedViewsMenu(!showSavedViewsMenu);
    }, [showSavedViewsMenu]);

    const hasAggregationSetup = useMemo(
      () =>
        tableConfig.aggregation ||
        columnsHaveAggregationSetup(fixedColumns) ||
        columnsHaveAggregationSetup(columns),
      [fixedColumns, columns]
    );

    const isSelectAllCheckboxShown =
      (onClickUpdate || onClickBatchEdit) && !pagination && !group;

    return (
      <div className="flex flex-row flex-nowrap items-center justify-between border-foreground/20 border-b py-2 text-muted-foreground">
        <HeaderGroup className="justify-start" data-is-left>
          {[
            // allow select all only if onClickUpdate or onClickBatchEdit function are provided
            // AND there is no pagination or grouping
            isSelectAllCheckboxShown && (
              <TableHeaderElement
                clicked={nbRowsSelected > 0}
                icon={
                  nbRowsSelected === nbRows
                    ? HEADER_CHECKBOX
                    : HEADER_CHECKBOX_INDETERMINATE
                }
                label={
                  headerConfig.isCompact === true
                    ? ''
                    : `${formatNumber(nbRowsSelected)} rows selected`
                }
                onClick={toggleSelectAll}
              />
            ),
            nbRows > 0 && allowDisplayDiffs && (
              <TableHeaderElement
                clicked={displayDiffs}
                icon="lucide_Eye"
                iconIfClicked="lucide_EyeOff"
                label="Initial values"
                onClick={toggleDisplayDiffs}
              />
            ),
            onClickNew && (
              <TableHeaderElement
                className="bg-accent px-3 font-semibold outline outline-accent hover:bg-accent/70 hover:outline-border"
                clicked={newClicked}
                icon="AddCircle"
                label={headerConfig.newLabel || 'New'}
                onClick={onClickNew}
              />
            ),
            onClickUpdate && (
              <TableHeaderElement
                className={cn(
                  'transition-colors',
                  nbRowsSelected > 0 && 'text-primary'
                )}
                clicked={updateClicked}
                disabled={!!updateDisabledReason}
                icon="CircleUpload"
                label={updateButtonLabel}
                onClick={onClickUpdate}
                tooltip={updateDisabledReason || undefined}
              />
            ),
            onClickBatchEdit && nbRowsSelected > 0 && (
              <TableHeaderElement
                clicked={batchEditClicked}
                icon="lucide_Pen"
                label={headerConfig.isCompact ? null : 'Edit selection'}
                onClick={onClickBatchEdit}
              />
            ),
            env.FEATURE_FLAGS.USE_ARCHIVAGE_FEATURE &&
              updateRowsArchiveStatus &&
              nbRowsSelected > 0 && (
                <TableHeaderElement
                  icon="lucide_Archive"
                  label={
                    shrinkRightIcons ? null : getArchiveLabel(archiveClicked)
                  }
                  onClick={() => updateRowsArchiveStatus(!archiveClicked)}
                  tooltip={`${archiveClicked ? 'Unarchive' : 'Archive'} selected rows`}
                />
              ),
            onDeleteSelectedRows && nbRowsSelected > 0 && (
              <TableHeaderElement
                className="hover:bg-destructive hover:text-destructive-foreground"
                clicked={onDeleteSelectedRows}
                icon="lucide_Trash2"
                onClick={onDeleteSelectedRows}
                tooltip="Delete selected rows"
              />
            )
          ].map((tableHeaderElement, index, arr) => (
            <Fragment key={index}>
              {!!index && arr[index - 1] && <HeaderElementSeparator />}
              {tableHeaderElement}
            </Fragment>
          ))}
        </HeaderGroup>
        <HeaderGroup data-is-right>
          {
            // allow aggregation only if there is no pagination or grouping
            // and if aggregation has been setup on at least one column
            !(pagination || group) && hasAggregationSetup && (
              <>
                <div className="flex items-center space-x-2">
                  <Label className="text-xs" htmlFor="toggle-show-aggregation">
                    {tableConfig.aggregation?.label || 'Show sub-totals'}
                  </Label>
                  <Switch
                    checked={showAggregation}
                    data-cy="toggle-show-aggregation"
                    id="toggle-show-aggregation"
                    onCheckedChange={toggleShowAggregation}
                  />
                </div>
                <HeaderGroupSeparator />
              </>
            )
          }
          <AnimatePresence>
            {(!isSelectAllCheckboxShown || headerConfig.isCompact === true) &&
              nbRowsSelected > 0 && (
                <motion.div
                  animate={{
                    opacity: 1
                  }}
                  className="flex items-center"
                  exit={{
                    opacity: 0
                  }}
                  initial={{
                    opacity: 0
                  }}
                  transition={{
                    duration: 0.1
                  }}
                >
                  <div className="px-2 font-semibold text-xs">
                    {`${nbRowsSelected} selected row${nbRowsSelected > 1 ? 's' : '\u00A0\u00A0'}`}
                  </div>
                  <HeaderGroupSeparator />
                </motion.div>
              )}
          </AnimatePresence>
          {nbRowsSelected === 2 && (
            <TableHeaderElement
              className=""
              icon="lucide_GitCompareArrows"
              label="Compare traces"
              onClick={onClickCompareSelectedRows}
              tooltip="Compare selected rows"
            />
          )}
          {nbRows === 'loading' ? (
            <div className="flex items-center gap-2 text-xs">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Skeleton className="mt-1 inline-block h-4 w-12" />
                  </TooltipTrigger>
                  <TooltipContent>Loading the rows count</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <HeaderGroupSeparator />
            </div>
          ) : (
            <>
              <div className="px-2 text-xs">
                {nbRows} {nbRows > 1 ? 'rows' : 'row'}
              </div>
              <HeaderGroupSeparator />
            </>
          )}

          <div>
            <Popover>
              <PopoverTrigger asChild>
                <div>
                  <TableHeaderElement
                    icon="lucide_Download"
                    label={shrinkRightIcons ? null : 'Export'}
                    tooltip="Export current table data"
                  />
                </div>
              </PopoverTrigger>
              <PopoverContent>
                <ExportTableMenu
                  changeTypeFile={changeTypeFile}
                  fileType={fileType}
                  onClick={onExport}
                />
              </PopoverContent>
            </Popover>
          </div>
          <HeaderGroupSeparator />
          <TableHeaderElement
            clicked={showFilters}
            icon="lucide_Funnel"
            label={shrinkRightIcons ? null : 'Filter rows'}
            onClick={toggleFilters}
            tooltip={`${showFilters ? 'Disable' : 'Applied'} filters${
              pagination ? '' : `\nare currently showing ${nbRows} rows`
            }`}
          />
          <HeaderElementSeparator />
          <TableHeaderElement
            clicked={isEditing}
            icon="lucide_PencilRuler"
            label={shrinkRightIcons ? null : 'Customize layout'}
            onClick={toggleEdition}
            tooltip="Reorder columns, as well as resize columns width and rows height"
          />
          {allowColumnsSelection && (
            <>
              <HeaderElementSeparator />
              <div>
                <Popover>
                  <PopoverTrigger asChild>
                    <div>
                      <TableHeaderElement
                        icon="lucide_Columns3Cog"
                        label={shrinkRightIcons ? null : 'Display'}
                        tooltip="Select the columns you want to display"
                      />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent>
                    <DisplayMenu
                      disableGroupBy={headerConfig.fastOverview}
                      displayConfig={displayConfig}
                      setGroupBy={setGroupBy}
                      tableConfig={tableConfig}
                      toggleColumnSelection={toggleColumnSelection}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {env.FEATURE_FLAGS.USE_ARCHIVAGE_FEATURE && onClickArchive && (
                <>
                  <HeaderElementSeparator />
                  <ArchiveDisplayMenu
                    archiveClicked={archiveClicked}
                    onClickArchive={onClickArchive}
                    shrinkRightIcons={shrinkRightIcons}
                  />
                </>
              )}
            </>
          )}
          {
            // Reset button
            resetDisplayConfig && (
              <>
                <HeaderElementSeparator />
                <TableHeaderElement
                  clicked={false}
                  icon="lucide_Undo2"
                  label={shrinkRightIcons ? null : 'Reset'}
                  onClick={resetDisplayConfig}
                  tooltip="Reset initial table view"
                />
              </>
            )
          }
          {allowColumnsSelection && (
            <>
              <HeaderGroupSeparator />
              <div>
                <TableHeaderElement
                  clicked={showSavedViewsMenu}
                  icon="lucide_Save"
                  label={shrinkRightIcons ? null : 'Saved views'}
                  onClick={toggleShowSavedViewsMenu}
                  tooltip="Create new table views or load existing ones"
                />
                {showSavedViewsMenu && (
                  //   <Popover>
                  //   <PopoverTrigger asChild>
                  //     <div>
                  //       <TableHeaderElement
                  //         label={shrinkRightIcons ? null : 'Display'}
                  //         icon="TableColumns"
                  //         tooltip="Select the columns you want to display"
                  //       />
                  //     </div>
                  //   </PopoverTrigger>
                  //   <PopoverContent>
                  //     <DisplayMenu
                  //       tableConfig={tableConfig}
                  //       displayConfig={displayConfig}
                  //       toggleColumnSelection={toggleColumnSelection}
                  //       setGroupBy={setGroupBy}
                  //     />
                  //   </PopoverContent>
                  // </Popover>
                  <div className="relative">
                    <div
                      className="fixed inset-0 z-5"
                      onClick={toggleShowSavedViewsMenu}
                    />
                    <ContextMenu>
                      <SavedViewsMenu
                        resetDisplayConfig={resetDisplayConfig}
                        saveConfig={saveConfig}
                        setVisible={setShowSavedViewsMenu}
                        updateUserDisplayConfig={updateUserDisplayConfig}
                      />
                    </ContextMenu>
                  </div>
                )}
              </div>
            </>
          )}

          {onClickConfig && (
            <>
              <HeaderGroupSeparator />
              <TableHeaderElement
                clicked={configClicked}
                icon="lucide_Cog"
                label={shrinkRightIcons ? null : 'Config'}
                onClick={onClickConfig}
              />
            </>
          )}
        </HeaderGroup>
      </div>
    );
  }
);
TableHeaders.propTypes = {
  toggleFilters: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  toggleEdition: PropTypes.func.isRequired,
  tableConfig: PropTypes.object.isRequired,
  displayConfig: PropTypes.object.isRequired,
  toggleColumnSelection: PropTypes.func.isRequired,
  setGroupBy: PropTypes.func.isRequired,
  nbRows: PropTypes.number.isRequired,
  update: PropTypes.object.isRequired,
  displayDiffs: PropTypes.bool.isRequired,
  toggleDisplayDiffs: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onClickCompareSelectedRows: PropTypes.func,
  onDeleteSelectedRows: PropTypes.func,
  showAggregation: PropTypes.bool,
  toggleShowAggregation: PropTypes.func.isRequired,
  nbRowsSelected: PropTypes.number.isRequired,
  toggleSelectAll: PropTypes.func.isRequired,
  resetDisplayConfig: PropTypes.func,
  shrinkRightIcons: PropTypes.bool,
  saveConfig: PropTypes.func,
  updateUserDisplayConfig: PropTypes.func,
  changeTypeFile: PropTypes.func,
  fileType: PropTypes.string,
  headerConfig: PropTypes.shape({
    isCompact: PropTypes.bool,
    fastOverview: PropTypes.bool,
    newLabel: PropTypes.string
  })
};

export default TableHeaders;

const HeaderElementSeparator = () => <div className="w-2" />;

const HeaderGroupSeparator = () => (
  <div className="mx-2 h-5 w-px border-muted-foreground/40 border-l" />
);

const HeaderGroup = ({ children, className }) => (
  <div
    className={cn(
      'mx-2 flex flex-row flex-nowrap items-center justify-end text-muted-foreground',
      className
    )}
  >
    {children}
  </div>
);
